import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Phone, KeyRound, ArrowLeft, Mail, LogIn } from "lucide-react";
import { toast, Toaster } from "sonner";
import { LOGO_URL, ACADEMY_NAME } from "@/lib/brand";

export default function StudentLogin() {
  const [mode, setMode] = useState("email"); // "email" | "otp"
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState(1);
  const [devOtp, setDevOtp] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const { sendOtp, verifyOtp, loginStudentEmail } = useAuth();
  const navigate = useNavigate();

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const res = await sendOtp(phone);
      setDevOtp(res.dev_otp || "");
      setStep(2);
      toast.success("OTP sent");
    } catch (e) {
      const msg = e.response?.data?.detail || "Failed to send OTP";
      setErr(typeof msg === "string" ? msg : "Failed");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await verifyOtp(phone, otp);
      toast.success("Welcome!");
      navigate("/portal");
    } catch (e) {
      const msg = e.response?.data?.detail || "Invalid OTP";
      setErr(typeof msg === "string" ? msg : "Invalid OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await loginStudentEmail(email.trim().toLowerCase(), password);
      toast.success("Welcome!");
      navigate("/portal");
    } catch (e) {
      const msg = e.response?.data?.detail || "Invalid email or password";
      setErr(typeof msg === "string" ? msg : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (m) => {
    setMode(m);
    setErr("");
    setStep(1);
    setOtp("");
    setDevOtp("");
    setPassword("");
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6 py-12">
      <div className="w-full max-w-md">
        <button onClick={() => navigate("/login")} className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-8" data-testid="back-to-admin-login">
          <ArrowLeft size={16} /> Back to admin login
        </button>
        <div className="bg-white border border-slate-200 rounded-lg p-8 shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <img src={LOGO_URL} alt="logo" className="w-12 h-12 object-contain" />
            <div>
              <h1 className="text-2xl font-bold" style={{ fontFamily: "Chivo" }}>{ACADEMY_NAME}</h1>
              <p className="text-xs text-slate-500">
                {mode === "email"
                  ? "Sign in with your email and password"
                  : (step === 1 ? "Sign in with your phone number" : `Code sent to ${phone}`)}
              </p>
            </div>
          </div>

          {/* Mode toggle */}
          <div className="grid grid-cols-2 gap-2 p-1 bg-slate-100 rounded-md mb-6" data-testid="login-mode-toggle">
            <button
              type="button"
              onClick={() => switchMode("email")}
              data-testid="mode-email"
              className={`py-2 px-3 text-sm font-medium rounded transition ${mode === "email" ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-900"}`}
            >
              <Mail size={14} className="inline mr-2" /> Email
            </button>
            <button
              type="button"
              onClick={() => switchMode("otp")}
              data-testid="mode-otp"
              className={`py-2 px-3 text-sm font-medium rounded transition ${mode === "otp" ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-900"}`}
            >
              <Phone size={14} className="inline mr-2" /> Phone OTP
            </button>
          </div>

          {mode === "otp" && step === 1 && (
            <form onSubmit={handleSendOtp} className="space-y-4">
              <div>
                <Label htmlFor="phone" className="label-caps">Phone Number</Label>
                <Input id="phone" data-testid="student-phone-input" type="tel" placeholder="10-digit phone number" value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))} className="mt-2 h-12 text-lg font-mono" maxLength={10} required />
              </div>
              {err && <div className="text-sm text-red-600" data-testid="student-login-error">{err}</div>}
              <Button type="submit" disabled={loading || phone.length < 10} data-testid="send-otp-button" className="w-full h-12 bg-slate-900 hover:bg-slate-800">
                {loading ? "Sending..." : <><Phone size={16} className="mr-2" /> Send OTP</>}
              </Button>
            </form>
          )}

          {mode === "otp" && step === 2 && (
            <form onSubmit={handleVerifyOtp} className="space-y-4">
              <div>
                <Label htmlFor="otp" className="label-caps">6-Digit OTP</Label>
                <Input id="otp" data-testid="otp-input" type="text" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} className="mt-2 h-14 text-center text-2xl tracking-[0.5em] font-mono" maxLength={6} required autoFocus />
              </div>
              {devOtp ? (
                <div className="text-xs p-3 bg-amber-50 border border-amber-200 rounded-md text-amber-800" data-testid="dev-otp-display">
                  Demo OTP (mock SMS): <span className="font-mono font-bold">{devOtp}</span>
                </div>
              ) : (
                <div className="text-xs p-3 bg-lime-50 border border-lime-200 rounded-md text-lime-800" data-testid="otp-info">
                  A 6-digit code has been sent to your mobile via SMS.
                </div>
              )}
              {err && <div className="text-sm text-red-600" data-testid="otp-error">{err}</div>}
              <Button type="submit" disabled={loading || otp.length < 6} data-testid="verify-otp-button" className="w-full h-12 bg-slate-900 hover:bg-slate-800">
                <KeyRound size={16} className="mr-2" /> {loading ? "Verifying..." : "Verify & Sign in"}
              </Button>
              <button type="button" onClick={() => { setStep(1); setOtp(""); setDevOtp(""); }} className="text-xs text-slate-500 hover:text-slate-900" data-testid="change-phone-button">
                Use a different number
              </button>
            </form>
          )}

          {mode === "email" && (
            <form onSubmit={handleEmailLogin} className="space-y-4">
              <div>
                <Label htmlFor="s-email" className="label-caps">Email</Label>
                <Input id="s-email" data-testid="student-email-input" type="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-2 h-12" autoComplete="username" required />
              </div>
              <div>
                <Label htmlFor="s-pass" className="label-caps">Password</Label>
                <Input id="s-pass" data-testid="student-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-2 h-12" autoComplete="current-password" required />
              </div>
              <div className="text-xs p-3 bg-slate-50 border border-slate-200 rounded-md text-slate-600">
                Don&apos;t have login credentials yet? Ask the academy admin to set your email and password, or use <button type="button" onClick={() => switchMode("otp")} className="text-slate-900 font-semibold underline">Phone OTP</button> instead.
              </div>
              {err && <div className="text-sm text-red-600" data-testid="email-login-error">{err}</div>}
              <Button type="submit" disabled={loading || !email || password.length < 6} data-testid="email-login-button" className="w-full h-12 bg-slate-900 hover:bg-slate-800">
                <LogIn size={16} className="mr-2" /> {loading ? "Signing in..." : "Sign In"}
              </Button>
            </form>
          )}

          <div className="mt-6 pt-6 border-t border-slate-100 text-center">
            <p className="text-sm text-slate-500">New here?</p>
            <button onClick={() => navigate("/register")} data-testid="goto-register" className="mt-2 text-sm font-semibold text-slate-900 hover:underline">
              Register as a new student →
            </button>
          </div>
        </div>
      </div>
      <Toaster position="top-right" richColors />
    </div>
  );
}
